export interface PlayerPublic {
  id: number
  first_name: string
  username: string | null
  photo_url: string | null
  level: number | null
  /** Нужен только для микст-турниров */
  gender: 'm' | 'f' | null
  level_confidence: number
  rated_matches: number
  side: 'left' | 'right' | 'both'
  games_played: number
  reliability: number
}

export interface PlayerMe extends PlayerPublic {
  /** Пусто у вошедших через Apple, пока не привязан Telegram */
  telegram_id: number | null
  /** «Пригласить друзей»: личные ссылки и сколько человек пришло по ним */
  invite_link_tg: string
  invite_link_web: string
  referrals: number
  has_apple: boolean
  city: string
  language: 'ru' | 'en' | 'es'
  is_admin: boolean
  /** Анкета тренера: pending | approved | rejected | paused; null — не тренер */
  coach_status: CoachStatus | null
  /** Может проводить турниры (разрешение администратора или одобренный тренер) */
  can_organize: boolean
  organizer_status: OrganizerStatus | null
  /** Заявки на корт (v0.35): клубы, где я отвечаю на заявки, и мои активные заявки */
  managed_clubs: ManagedClub[]
  bookings_active: number
}

export interface Club {
  id: number
  name: string
  city: string
  address: string | null
  lat: number | null
  lon: number | null
  price_per_hour: number | null
  indoor: boolean
  courts: number
  booking_url: string | null
  phone: string | null
  maps_url: string | null
  note: string | null
}

export interface Slot {
  id: number
  status: 'joined' | 'waitlist' | 'cancelled'
  position: number
  paid: boolean
  /** Место на корте до матча: 1–2 пара 1 (слева, справа), 3–4 пара 2; null — не выбрано */
  court_spot: number | null
  player: PlayerPublic
}

export interface Game {
  id: number
  starts_at: string
  duration_min: number
  level_min: number
  level_max: number
  price_per_player: number | null
  max_players: number
  status: 'open' | 'full' | 'cancelled' | 'played' | 'running' | 'finished'
  notes: string | null
  court_booked: boolean
  club: Club
  captain: PlayerPublic
  slots: Slot[]
  joined_count: number
  waitlist_count: number
  my_status: 'joined' | 'waitlist' | null
  fits_my_level: boolean
  is_captain: boolean
  share_url: string | null
  /** Ссылка для отправки вне Telegram (WhatsApp): страница на нашем домене */
  web_url: string | null
  kind: GameKind
  points_per_match: number | null
  /** Как считается матч турнира: геймы или очки (americano — всегда очки) */
  score_mode: ScoreMode | null
  rounds: number | null
  courts: number | null
  rated: boolean
  /** Микст-турнир пар: мест поровну для М и Ж, пары назначает организатор */
  mixed: boolean
  /** Чат доступен составу и резерву; непрочитанные — чужие сообщения */
  chat_available: boolean
  chat_unread: number
  /** Объявление капитана — закреплённое сообщение чата */
  pinned_text: string | null
  /** Зритель в составе и запись ещё открыта */
  can_invite: boolean
  can_remove: boolean
  /** Игра на четверых до начала: можно выбирать места на корте */
  spots_open: boolean
  removed_by_captain: boolean
  /** Зрителя позвали сюда, и он ещё не ответил */
  my_invite_id: number | null
  invited_by: string | null
}

export interface ChatMessage {
  id: number
  player_id: number
  first_name: string
  photo_url: string | null
  text: string
  created_at: string
  mine: boolean
}

export interface Chat {
  messages: ChatMessage[]
  pinned: ChatMessage | null
  has_more: boolean
  is_captain: boolean
}

export interface InviteCandidate {
  id: number
  first_name: string
  photo_url: string | null
  level: number
  played_together: boolean
  fits_level: boolean
  invite_status: 'pending' | 'accepted' | 'declined' | null
}

export interface GameInvite {
  id: number
  status: 'pending' | 'accepted' | 'declined'
  invitee_id: number
  invitee_name: string
  invitee_photo: string | null
  invitee_level: number | null
  inviter_name: string
}

export interface MyInvite {
  id: number
  inviter_name: string
  inviter_photo: string | null
  game: Game
}

export type GameKind = 'game' | 'americano' | 'mexicano' | 'pairs' | 'king' | 'training'

/** Турниры: americano и mexicano — личный зачёт, pairs — постоянные пары */
export const isTournamentKind = (kind: string): boolean =>
  kind === 'americano' || kind === 'mexicano' || kind === 'pairs' || kind === 'king'
/** Постоянные пары: турнир пар и «Король корта» */
export const isPairsKind = (kind: string): boolean => kind === 'pairs' || kind === 'king'
/** Раунды строит организатор по кнопке */
export const isRoundByRound = (kind: string): boolean => kind === 'mexicano' || kind === 'king'
export const isIndividualKind = (kind: string): boolean => kind === 'americano' || kind === 'mexicano'

export interface SetScore {
  a: number
  b: number
  tiebreak: boolean
}

export type Reason = 'better' | 'worse' | 'as_expected' | 'win_guarded' | 'loss_guarded'

/** Почему уровень изменился — текст строится на фронте по reason */
export interface Explanation {
  expected: number
  actual: number
  team_level: number
  opp_level: number
  reason: Reason
  capped: boolean
  calibrating: boolean
}

export interface MatchPlayer {
  id: number
  first_name: string
  level_before: number | null
  level_after: number | null
  delta: number | null
  /** true — матч не подтверждён, delta — прогноз */
  projected: boolean
  why: Explanation | null
}

export interface MatchPreview {
  winner: 'a' | 'b' | 'draw'
  sets_a: number
  sets_b: number
  games_a: number
  games_b: number
  team_a: MatchPlayer[]
  team_b: MatchPlayer[]
  /** Товарищеский матч: уровень не меняется */
  friendly: boolean
}

export interface Match {
  id: number
  game_id: number
  status: 'pending' | 'confirmed' | 'disputed' | 'withdrawn'
  winner: 'a' | 'b' | 'draw'
  sets: SetScore[]
  games_a: number
  games_b: number
  team_a: MatchPlayer[]
  team_b: MatchPlayer[]
  submitted_by: number
  submitted_by_name: string
  created_at: string
  auto_confirm_at: string | null
  can_confirm: boolean
  can_withdraw: boolean
  /** Оспорить может любой из четверых, кроме автора; после подтверждения — 48 ч */
  can_dispute: boolean
  dispute_until: string | null
  /** Товарищеский матч: уровень не меняется */
  friendly: boolean
}

export interface RatingHistoryItem {
  match_id: number
  game_id: number
  created_at: string
  level_before: number
  level_after: number
  delta: number
  expected: number
  actual: number
  why: Explanation | null
  result: 'win' | 'loss' | 'draw'
  score: string
  partner: string
  opponents: string
}

export type LevelSource = 'playtomic' | 'manual' | 'survey'

export type OrganizerStatus = 'pending' | 'approved' | 'rejected' | 'revoked'

export interface AdminPlayer {
  id: number
  first_name: string
  username: string | null
  photo_url: string | null
  level: number | null
  level_source: LevelSource | 'admin' | null
  rated_matches: number
  games_played: number
  created_at: string
  organizer_status: OrganizerStatus | null
  organizer_requested_at: string | null
}

export interface OnboardingAnswers {
  /** playtomic | manual — игрок называет уровень сам; survey — считаем по ответам */
  source: LevelSource
  level: number | null
  months_playing: number
  games_per_month: number
  racket_sports_background: boolean
  side: 'left' | 'right' | 'both'
  language: 'ru' | 'en' | 'es'
  city: string
  /** Откуда пришёл и кто позвал (v0.34) */
  ref_source?: string | null
  referrer_id?: number | null
}

/** Профиль: матч глазами игрока */
export interface RecordMatch {
  match_id: number
  game_id: number
  played_at: string
  club: string
  status: 'pending' | 'confirmed' | 'disputed'
  result: 'win' | 'loss' | 'draw'
  score: string
  partner: string
  opponents: string[]
  delta: number | null
  level_after: number | null
  projected: boolean
  why: Explanation | null
  /** Товарищеский матч: уровень не меняется */
  friendly: boolean
}

export interface RecordSummary {
  matches: number
  wins: number
  losses: number
  draws: number
  games_won: number
  games_lost: number
  delta_30d: number
  best_level: number | null
}

export interface RatingPoint {
  at: string
  level: number
  delta: number | null
  match_id: number | null
}

export interface RecordTournament {
  game_id: number
  kind: GameKind
  played_at: string
  club: string
  place: number
  of: number
  matches: number
  wins: number
  delta: number | null
  level_after: number | null
}

export interface RecordGame {
  game_id: number
  played_at: string
  club: string
  rated: boolean
  players: string[]
  matches: Match[]
}

export interface PlayerRecord {
  games: RecordGame[]
  tournaments: RecordTournament[]
  summary: RecordSummary
  chart: RatingPoint[]
  matches: RecordMatch[]
}

/** Турнир: расписание, таблица, права зрителя */
export interface TPlayer {
  id: number
  first_name: string
  level: number | null
  photo_url: string | null
  delta: number | null
  level_after: number | null
}

export interface TMatch {
  id: number
  round: number
  court: number
  a: TPlayer[]
  b: TPlayer[]
  team_a: number | null
  team_b: number | null
  score_a: number | null
  score_b: number | null
  /** «Король корта»: кто выиграл золотое очко при равном счёте */
  golden: 'a' | 'b' | null
}

export interface TRow {
  place: number
  key: number
  players: TPlayer[]
  played: number
  wins: number
  draws: number
  losses: number
  points_for: number
  points_against: number
  diff: number
  /** «Король корта»: раундов на корте 1 */
  crowns: number
}

export type ScoreMode = 'games' | 'points'

/** Строка таблицы после раунда N; игроки — в standings по key */
export interface TRoundRow {
  key: number
  place: number
  played: number
  wins: number
  draws: number
  losses: number
  points_for: number
  points_against: number
  /** На сколько мест поднялся (+) или опустился (−) за раунд */
  move: number
}

export interface TRoundTable {
  round: number
  complete: boolean
  rows: TRoundRow[]
}

export interface TPlan {
  players: number
  rounds: number
  courts: number
  matches: number
  per_player: number
  resting: number
}

export interface TSettings {
  points_per_match?: number
  score_mode?: ScoreMode
  max_players?: number
  /** 0 — авто, −1 — без лимита (mexicano) */
  rounds?: number
  /** 0 — авто */
  courts?: number
  /** null — без взноса */
  price_per_player?: number | null
  notes?: string
  duration_min?: number
  court_booked?: boolean
  /** «Король корта»: минут на раунд (таймер) */
  round_minutes?: number
}

/** Итоги турнира файлом: подписанная ссылка и имя файла */
export interface ReportFile {
  url: string
  name: string
}

export interface ReportLinks {
  png: ReportFile
  pdf: ReportFile
}

export interface Tournament {
  game_id: number
  kind: GameKind
  status: Game['status']
  points_per_match: number | null
  score_mode: ScoreMode
  rounds_setting: number | null
  courts_setting: number | null
  max_players: number
  rated: boolean
  rounds_total: number
  scored: number
  total_matches: number
  current_round: number | null
  matches: TMatch[]
  standings: TRow[]
  can_manage: boolean
  can_start: boolean
  start_problem: string | null
  my_why: Explanation | null
  mixed: boolean
  /** До старта микста: пары [М, Ж], назначенные организатором */
  draft_pairs: [number, number][]
  round_tables: TRoundTable[]
  plan: TPlan | null
  /** Отдыхают в раунде: { раунд: [id игрока] } */
  resting: Record<string, number[]>
  /** Сколько раундов всего (mexicano: null — без лимита) */
  rounds_planned: number | null
  can_next_round: boolean
  can_undo_round: boolean
  next_round_problem: string | null
  /** Организатор может исправить счёт завершённого турнира */
  can_correct: boolean
  /** Таймер раунда («Король корта»): минут на раунд, когда запущен, для какого раунда */
  round_minutes: number | null
  timer_started_at: string | null
  timer_round: number | null
  /** Время сервера в момент ответа — отсчёт не зависит от часов телефона */
  server_now: string | null
}

/** Открытый матч клуба из Playtomic (по ключу клуба). Только показ. */
export interface ExternalGame {
  id: number
  source: string
  club: Club
  court_name: string | null
  starts_at: string
  duration_min: number
  players_count: number
  max_players: number
  free: number
  level_min: number | null
  level_max: number | null
  url: string | null
}

/** Игрок, чьи сообщения я скрыл. */
export interface BlockedPlayer {
  id: number
  first_name: string
  photo_url: string | null
}

/** Скидка на падел-товар (тестовая полоса в ленте). */
export interface Deal {
  id: number
  title: string
  shop: string
  url: string
  image_url: string | null
  price: number
  old_price: number | null
  discount: number | null
  category: string
}

export interface AdminDeal extends Deal {
  active: boolean
  expires_at: string | null
  created_at: string
  clicks: number
  people: number
}

export interface DealInput {
  url: string
  title: string
  shop: string
  image_url: string | null
  price: number
  old_price: number | null
  category: string
  expires_at: string | null
  active: boolean
}

// --- Тренеры ---

export type CoachFormat = 'single' | 'split' | 'group'
export type CoachStatus = 'pending' | 'approved' | 'rejected' | 'paused'

export interface ClubShort {
  id: number
  name: string
  city: string
}

export interface Coach {
  id: number
  player_id: number
  display_name: string
  photo_url: string | null
  bio: string
  certificate: string | null
  experience_years: number | null
  languages: string[]
  clubs: ClubShort[]
  level_min: number
  level_max: number
  formats: CoachFormat[]
  price_single: number | null
  price_split: number | null
  price_group: number | null
}

export interface MyCoachRequest {
  id: number
  status: 'new' | 'accepted' | 'declined'
  format: CoachFormat
  created_at: string
  coach_link: string | null
}

export interface CoachListItem extends Coach {
  my_request: MyCoachRequest | null
}

export interface MyCoach extends Coach {
  status: CoachStatus
  club_ids: number[]
  new_requests: number
}

export interface AdminCoach extends MyCoach {
  player_name: string
  requests: number
  accepted: number
}

export interface CoachInput {
  display_name: string
  bio: string
  certificate: string | null
  experience_years: number | null
  languages: string[]
  club_ids: number[]
  level_min: number
  level_max: number
  formats: CoachFormat[]
  price_single: number | null
  price_split: number | null
  price_group: number | null
}

export interface CoachRequestItem {
  id: number
  status: 'new' | 'accepted' | 'declined'
  format: CoachFormat
  when_text: string | null
  message: string | null
  created_at: string
  answered_at: string | null
  player_id: number
  player_name: string
  player_level: number | null
  player_photo: string | null
  player_link: string | null
}

// --- Заявки на корт (v0.35) ---

export interface ManagedClub {
  id: number
  name: string
  pending: number
}

export interface BookingPrice {
  minutes: number
  amount: number | null
}

export interface BookingClub {
  id: number
  name: string
  city: string
  address: string | null
  maps_url: string | null
  courts: string[]
  durations: number[]
  prices: BookingPrice[]
  price_note: string | null
  open: string
  close: string
  step: number
  horizon_days: number
  cancel_hours: number
  reply_hours: number
  public: boolean
  is_manager: boolean
}

export interface BookingSlot {
  at: string
  /** «18:00» по времени клуба */
  label: string
  /** Свободные корты (номера с 1); пусто — занято или уже поздно */
  free: number[]
}

export interface BookingSlots {
  club: BookingClub
  date: string
  minutes: number
  slots: BookingSlot[]
}

export type BookingStatus = 'requested' | 'confirmed' | 'declined' | 'cancelled' | 'expired'

export interface BookingPayment {
  iban: string | null
  holder: string | null
  concept: string
  amount: number | null
}

export interface Booking {
  id: number
  club_id: number
  club_name: string
  club_city: string
  maps_url: string | null
  court: number
  court_name: string
  starts_at: string
  ends_at: string
  minutes: number
  status: BookingStatus
  amount: number | null
  price_note: string | null
  note: string | null
  game_id: number | null
  created_at: string
  reply_by: string | null
  cancel_free_until: string | null
  late_cancel: boolean
  cancelled_by: 'player' | 'club' | 'calendar' | null
  no_show: boolean
  can_cancel: boolean
  payment: BookingPayment | null
}

export interface DeskBooking extends Booking {
  player_id: number
  player_name: string
  player_username: string | null
  player_level: number | null
  player_reliability: number
  can_answer: boolean
  can_club_cancel: boolean
  can_no_show: boolean
  calendar_state: 'push' | 'delete' | 'done' | 'error' | null
  calendar_error: string | null
}

export interface DeskOut {
  club: BookingClub
  items: DeskBooking[]
  calendar: { enabled: boolean; calendar_id: string | null; service_email: string | null; synced_at: string | null }
  email: string | null
  link_tg: string | null
  link_web: string
}
